/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useComposer — the 5-step Composer state machine (single-image + carousel).
 *
 *   Setup       pick bucket, refs, subject, post kind (single/carousel + slide count)
 *   Narrative   (carousel only) Claude proposes N slide outlines, user edits
 *   Prompts     Claude drafts a Nano Banana Pro prompt per slide
 *   Images      Nano Banana Pro renders each slide; slides 1..N pass slide 0
 *               as an anchor image so linen/lighting/palette stays consistent
 *   Caption     Claude Haiku drafts caption + hashtags for the whole post
 *
 * Slide 0 state (single = only slide 0) is treated identically to the old
 * single-image flow, so the auto-pick + hand-picked ref logic still applies
 * for its prompt.
 */
import { useCallback, useState } from 'react';
import { marketing } from '@/lib/supabase/marketing';
import { errorMessage } from '@/lib/utils';

export type ComposerStep = 'setup' | 'narrative' | 'prompts' | 'images' | 'caption' | 'done';
export type PostKind = 'single' | 'carousel';
export type AspectRatio = '1:1' | '4:5' | '3:4' | '4:3' | '9:16' | '16:9';

export interface SlideState {
  index: number;
  role: string;            // "hero" | "cheese pull" | "crumb macro" | ...
  subjectHint: string;     // slide 0 = the dish; slides 1..N inherit from slide 0
  framingDirective: string; // camera-only instruction for slides 1..N (carousel)
  prompt: string | null;
  styleNotes: string[];
  refsUsed: string[];
  imageB64: string | null;
  imageMime: string;
  imageMs: number | null;
}

export interface ComposerState {
  step: ComposerStep;
  bucketSlug: string | null;
  productId: string | null;
  subCategory: string | null;
  subjectHint: string;
  pickedReferenceIds: string[];
  /** Optional ONE reference whose ingredient composition (arrangement,
   * layering, portion, garnish) Claude will copy — style still comes from
   * pickedReferenceIds. Null = don't anchor composition. */
  compositionAnchorRefId: string | null;
  /** When true (and anchor is set), the anchor's plating vessel + garnish
   * placement are also copied. Style (linen, lighting, camera) still comes
   * from the style refs. */
  anchorCarryVessel: boolean;
  postKind: PostKind;
  slideCount: number;      // 1 for single, 2-10 for carousel
  aspectRatio: AspectRatio; // pinned per-post so Meta doesn't crop carousels
  slides: SlideState[];
  draftId: string | null;
  caption: string | null;
  hashtags: string[];
  captionMs: number | null;
  busy: boolean;
  busySlide: number | null;
  error: string | null;
}

function emptySlide(index: number, role = index === 0 ? 'hero' : '', subject = '', framing = ''): SlideState {
  return { index, role, subjectHint: subject, framingDirective: framing, prompt: null, styleNotes: [], refsUsed: [], imageB64: null, imageMime: 'image/jpeg', imageMs: null };
}

const initial: ComposerState = {
  step: 'setup',
  bucketSlug: null,
  productId: null,
  subCategory: null,
  subjectHint: '',
  pickedReferenceIds: [],
  compositionAnchorRefId: null,
  anchorCarryVessel: false,
  postKind: 'single',
  slideCount: 1,
  aspectRatio: '4:5',
  slides: [emptySlide(0)],
  draftId: null,
  caption: null,
  hashtags: [],
  captionMs: null,
  busy: false,
  busySlide: null,
  error: null,
};

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const text = await res.text();
  let parsed: any;
  try { parsed = JSON.parse(text); } catch { throw new Error(`bad JSON from ${url}: ${text.slice(0, 200)}`); }
  if (!res.ok || parsed?.ok === false || parsed?.error) {
    throw new Error(parsed?.error ?? parsed?.detail ?? `HTTP ${res.status}`);
  }
  return parsed as T;
}

export function useComposer() {
  const [state, setState] = useState<ComposerState>(initial);

  const reset = useCallback(() => setState(initial), []);

  const configure = useCallback((patch: Partial<ComposerState>) => {
    setState((s) => {
      const next = { ...s, ...patch, error: null };
      // Keep slides array in sync when postKind / slideCount changes
      if ('postKind' in patch || 'slideCount' in patch) {
        const target = next.postKind === 'single' ? 1 : Math.min(Math.max(2, next.slideCount), 10);
        next.slideCount = target;
        if (next.slides.length !== target) {
          const slides: SlideState[] = [];
          for (let i = 0; i < target; i++) {
            slides.push(next.slides[i] ?? emptySlide(i));
          }
          next.slides = slides;
        }
      }
      return next;
    });
  }, []);

  const updateSlide = useCallback((index: number, patch: Partial<SlideState>) => {
    setState((s) => ({
      ...s,
      slides: s.slides.map((sl) => (sl.index === index ? { ...sl, ...patch } : sl)),
    }));
  }, []);

  // ── Setup → next: create draft, plan (carousel), or draft prompt (single)
  const startNext = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const { bucketSlug, productId, subjectHint, postKind, slideCount } = state;
      if (!bucketSlug) throw new Error('pick a bucket first');
      if (!subjectHint.trim()) throw new Error('describe the subject');

      const { data: draft, error } = await marketing
        .from('content_draft')
        .insert({ bucket_slug: bucketSlug, product_id: productId, post_kind: postKind, status: 'draft' })
        .select('id')
        .single();
      if (error) throw error;

      if (postKind === 'single') {
        // Same as before — hop straight to prompt draft for slide 0
        const promptRes = await postJson<{
          prompt: string; style_notes: string[]; sub_category: string | null;
          refs_used: Array<{ id: string; name: string }>;
        }>('/api/content/draft-prompt', {
          draft_id: draft.id,
          slide_index: 0,
          sub_category: state.subCategory || undefined,
          subject_hint: subjectHint,
          reference_ids: state.pickedReferenceIds.length > 0 ? state.pickedReferenceIds : undefined,
          composition_anchor_ref_id: state.compositionAnchorRefId || undefined,
          carry_vessel_from_anchor: state.anchorCarryVessel || undefined,
        });
        const refNames = (promptRes.refs_used ?? []).map((r) => typeof r === 'string' ? r : r.name);
        setState((s) => ({
          ...s,
          busy: false,
          step: 'prompts',
          draftId: draft.id,
          subCategory: promptRes.sub_category ?? s.subCategory,
          slides: [{ ...(s.slides[0] ?? emptySlide(0)), index: 0, role: 'hero', subjectHint, prompt: promptRes.prompt, styleNotes: promptRes.style_notes ?? [], refsUsed: refNames }],
        }));
      } else {
        // Carousel: ask Claude for the outline
        const plan = await postJson<{ slides: Array<{ index: number; role: string; subject_hint: string; framing_directive?: string }> }>(
          '/api/content/plan-carousel',
          { bucket_slug: bucketSlug, subject_hint: subjectHint, slide_count: slideCount },
        );
        const planned: SlideState[] = [];
        for (let i = 0; i < slideCount; i++) {
          const p = plan.slides.find((x) => x.index === i);
          planned.push({
            ...emptySlide(i),
            role: p?.role ?? (i === 0 ? 'hero' : `slide ${i + 1}`),
            subjectHint: i === 0 ? subjectHint : '',
            framingDirective: p?.framing_directive ?? '',
          });
        }
        setState((s) => ({ ...s, busy: false, step: 'narrative', draftId: draft.id, slides: planned }));
      }
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: (e as Error).message }));
    }
  }, [state]);

  // ── Narrative → Prompts: draft prompts for every slide sequentially
  const draftAllPrompts = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const { draftId, slides, subCategory, pickedReferenceIds } = state;
      if (!draftId) throw new Error('no draft');
      const nextSlides: SlideState[] = [];
      for (const sl of slides) {
        setState((s) => ({ ...s, busySlide: sl.index }));
        const isReframe = sl.index > 0;
        const res = await postJson<{
          prompt: string; style_notes: string[];
          refs_used: Array<{ id: string; name: string }> | string[];
        }>('/api/content/draft-prompt', {
          draft_id: draftId,
          slide_index: sl.index,
          // Slide 0 = the hero, gets the full compose treatment.
          // Slides 1..N = reframes of the hero; camera-only prompt, no new
          // subject, no Drive refs (the hero image IS the reference).
          is_carousel_reframe: isReframe || undefined,
          framing_directive: isReframe ? (sl.framingDirective || undefined) : undefined,
          sub_category: !isReframe ? (subCategory || undefined) : undefined,
          subject_hint: !isReframe ? sl.subjectHint : undefined,
          reference_ids: !isReframe && pickedReferenceIds.length > 0 ? pickedReferenceIds : undefined,
          composition_anchor_ref_id: !isReframe ? (state.compositionAnchorRefId || undefined) : undefined,
          carry_vessel_from_anchor: !isReframe ? (state.anchorCarryVessel || undefined) : undefined,
        });
        const refNames = (res.refs_used ?? []).map((r: any) => typeof r === 'string' ? r : r.name);
        nextSlides.push({ ...sl, prompt: res.prompt, styleNotes: res.style_notes ?? [], refsUsed: refNames });
      }
      setState((s) => ({ ...s, busy: false, busySlide: null, step: 'prompts', slides: nextSlides }));
    } catch (e) {
      setState((s) => ({ ...s, busy: false, busySlide: null, error: (e as Error).message }));
    }
  }, [state]);

  // ── Prompts → Images: generate slide 0 first, then slides 1..N anchored to slide 0
  const generateSlideImage = useCallback(async (slideIndex: number, editedPrompt?: string) => {
    setState((s) => ({ ...s, busy: true, busySlide: slideIndex, error: null }));
    try {
      const { draftId, slides } = state;
      if (!draftId) throw new Error('no draft');
      const slide = slides.find((s) => s.index === slideIndex);
      if (!slide) throw new Error('slide not found');
      const promptToUse = editedPrompt ?? slide.prompt ?? '';
      if (!promptToUse.trim()) throw new Error('prompt empty');

      // Persist edited prompt
      if (editedPrompt && editedPrompt !== slide.prompt) {
        await marketing
          .from('content_draft_slide')
          .update({ prompt: editedPrompt })
          .eq('draft_id', draftId)
          .eq('slide_index', slideIndex);
      }

      // Anchor slides 1..N on slide 0's image (if it exists)
      const anchor = slideIndex > 0 ? slides.find((s) => s.index === 0)?.imageB64 : null;

      const res = await postJson<{ b64: string; mime: string; response_ms: number }>(
        '/api/content/generate-image',
        {
          draft_id: draftId,
          slide_index: slideIndex,
          anchor_image_b64: anchor || undefined,
          anchor_slide_index: anchor ? 0 : undefined,
          aspect_ratio: state.aspectRatio,
        },
      );
      setState((s) => ({
        ...s,
        busy: false,
        busySlide: null,
        step: 'images',
        slides: s.slides.map((sl) => sl.index === slideIndex ? { ...sl, prompt: editedPrompt ?? sl.prompt, imageB64: res.b64, imageMime: res.mime, imageMs: res.response_ms } : sl),
      }));
    } catch (e) {
      setState((s) => ({ ...s, busy: false, busySlide: null, error: (e as Error).message }));
    }
  }, [state]);

  // Generate every slide in sequence (slide 0 first — the anchor).
  const generateAllImages = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const { draftId } = state;
      if (!draftId) throw new Error('no draft');
      for (let i = 0; i < state.slides.length; i++) {
        setState((s) => ({ ...s, busySlide: i }));
        // Read fresh state each iteration so anchor image is available.
        // Using a functional pattern: fire generateSlideImage which reads state.
        await new Promise<void>((resolve, reject) => {
          setState((s) => {
            const sl = s.slides.find((x) => x.index === i);
            if (!sl?.prompt) { reject(new Error(`slide ${i} has no prompt`)); return s; }
            const anchor = i > 0 ? s.slides.find((x) => x.index === 0)?.imageB64 : null;
            postJson<{ b64: string; mime: string; response_ms: number }>('/api/content/generate-image', {
              draft_id: s.draftId,
              slide_index: i,
              anchor_image_b64: anchor || undefined,
              anchor_slide_index: anchor ? 0 : undefined,
              aspect_ratio: s.aspectRatio,
            })
              .then((res) => {
                setState((cs) => ({
                  ...cs,
                  slides: cs.slides.map((sl2) => sl2.index === i ? { ...sl2, imageB64: res.b64, imageMime: res.mime, imageMs: res.response_ms } : sl2),
                }));
                resolve();
              })
              .catch(reject);
            return s;
          });
        });
      }
      setState((s) => ({ ...s, busy: false, busySlide: null, step: 'images' }));
    } catch (e) {
      setState((s) => ({ ...s, busy: false, busySlide: null, error: (e as Error).message }));
    }
  }, [state]);

  // ── Caption (unchanged)
  const draftCaption = useCallback(async (hint?: string) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const { draftId, slides } = state;
      if (!draftId) throw new Error('no draft');
      const hero = slides.find((s) => s.index === 0);
      const res = await postJson<{ caption: string; hashtags: string[]; response_ms: number }>(
        '/api/content/draft-caption',
        { draft_id: draftId, image_b64: hero?.imageB64, hint },
      );
      setState((s) => ({ ...s, busy: false, step: 'caption', caption: res.caption, hashtags: res.hashtags, captionMs: res.response_ms }));
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: (e as Error).message }));
    }
  }, [state]);

  const updateCaption = useCallback(async (caption: string, hashtags: string[]) => {
    setState((s) => ({ ...s, caption, hashtags }));
    if (state.draftId) {
      await marketing.from('content_draft').update({ caption, hashtags }).eq('id', state.draftId);
    }
  }, [state.draftId]);

  const finish = useCallback(() => setState((s) => ({ ...s, step: 'done' })), []);

  // Schedule the draft for later, or publish now (scheduledAt = null).
  // scheduledAt: ISO string in the future → status='scheduled', pg_cron picks it up.
  // scheduledAt: null → fires the worker immediately via /api/content/publish.
  const scheduleDraft = useCallback(async (scheduledAt: string | null): Promise<{ ok: boolean; error?: string; media_id?: string }> => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const { draftId } = state;
      if (!draftId) throw new Error('no draft');
      if (scheduledAt) {
        // Just flip to scheduled — cron worker will pick it up
        const { error } = await marketing
          .from('content_draft')
          .update({ status: 'scheduled', scheduled_at: scheduledAt, publish_error: null })
          .eq('id', draftId);
        if (error) throw error;
        setState((s) => ({ ...s, busy: false }));
        return { ok: true };
      } else {
        // Publish now — flip to scheduled + kick the worker directly
        const { error } = await marketing
          .from('content_draft')
          .update({ status: 'scheduled', scheduled_at: new Date().toISOString(), publish_error: null })
          .eq('id', draftId);
        if (error) throw error;
        const res = await postJson<{ processed: number; results: Array<{ ok: boolean; error?: string; media_id?: string }> }>(
          '/api/content/publish',
          { draft_id: draftId },
        );
        const r = res.results?.[0];
        setState((s) => ({ ...s, busy: false, error: r?.ok ? null : errorMessage(r?.error, 'publish failed') }));
        return { ok: !!r?.ok, error: r?.ok ? undefined : errorMessage(r?.error, 'publish failed'), media_id: r?.media_id };
      }
    } catch (e) {
      const msg = errorMessage(e);
      setState((s) => ({ ...s, busy: false, error: msg }));
      return { ok: false, error: msg };
    }
  }, [state]);

  return { state, reset, configure, updateSlide, startNext, draftAllPrompts, generateSlideImage, generateAllImages, draftCaption, updateCaption, finish, scheduleDraft };
}
